import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import StarRating from "../components/StarRating";
import api from "../lib/api";
import { useLang } from "../i18n";
import { ArrowLeft, Plus, Trash2, CalendarDays, MapPin, X, Skull, Pencil, ImagePlus, Loader2, ExternalLink, Eye, EyeOff } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [bands, setBands] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ band_id: "", date: "", rating: 0, comment: "", attended: true });
  const [showEventEdit, setShowEventEdit] = useState(false);
  const [eventForm, setEventForm] = useState(null);
  const [bandQuery, setBandQuery] = useState("");
  const [showBandSug, setShowBandSug] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/events/${id}`);
    setEvent(data);
  }, [id]);
  useEffect(() => {
    load();
    api.get("/bands").then(({ data }) => setBands(data));
  }, [load]);

  if (!event) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const eventDates = (() => {
    if (!event.start_date) return [];
    const out = [];
    const parse = (s) => { const [y, m, d] = s.slice(0, 10).split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
    const pad = (n) => String(n).padStart(2, "0");
    const start = parse(event.start_date);
    const end = event.end_date ? parse(event.end_date) : start;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
    }
    return out;
  })();
  const multiDay = eventDates.length > 1;

  const openAdd = () => {
    setEditingId(null);
    setForm({ band_id: "", date: event.start_date || "", rating: 0, comment: "", attended: true });
    setBandQuery(""); setShowBandSug(false);
    setShowAdd(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ band_id: c.band_id, date: c.date || event.start_date || "", rating: c.rating || 0, comment: c.comment || "", attended: c.attended !== false });
    setBandQuery(c.band?.name || ""); setShowBandSug(false);
    setShowAdd(true);
  };

  const bandMatches = (bandQuery.trim()
    ? bands.filter((b) => b.name.toLowerCase().includes(bandQuery.trim().toLowerCase()))
    : bands).slice(0, 8);

  const saveConcert = async () => {
    if (!form.band_id) return;
    const payload = { event_id: id, band_id: form.band_id, date: form.date || event.start_date, rating: form.rating, comment: form.comment, attended: form.attended };
    if (editingId) {
      await api.put(`/concerts/${editingId}`, payload);
    } else {
      await api.post("/concerts", payload);
    }
    setShowAdd(false);
    setEditingId(null);
    setForm({ band_id: "", date: "", rating: 0, comment: "", attended: true });
    load();
  };

  const toggleAttended = async (c) => {
    await api.put(`/concerts/${c.id}`, {
      event_id: id, band_id: c.band_id, date: c.date || event.start_date,
      rating: c.rating || 0, comment: c.comment || "", attended: !(c.attended !== false),
    });
    load();
  };

  const removeConcert = async (cid) => {
    await api.delete(`/concerts/${cid}`);
    load();
  };

  const removeEvent = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/events/${id}`);
    navigate("/calendar");
  };

  const openEventEdit = () => {
    setEventForm({
      name: event.name, type: event.type, start_date: event.start_date,
      end_date: event.end_date || "", location: event.location || "",
      url: event.url || "",
      rating: event.rating || 0, comment: event.comment || "",
    });
    setShowEventEdit(true);
  };

  const saveEvent = async () => {
    await api.put(`/events/${id}`, { ...eventForm, end_date: eventForm.end_date || null });
    setShowEventEdit(false);
    load();
  };

  const eventPayload = (over = {}) => ({
    name: event.name, type: event.type, start_date: event.start_date,
    end_date: event.end_date || null, location: event.location || "",
    url: event.url || "",
    rating: event.rating || 0, comment: event.comment || "",
    photos: event.photos || [], ...over,
  });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd);
      const url = data.url;
      await api.put(`/events/${id}`, eventPayload({ photos: [...(event.photos || []), url] }));
      load();
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (url) => {
    await api.put(`/events/${id}`, eventPayload({ photos: (event.photos || []).filter((p) => p !== url) }));
    load();
  };

  const inputCls = "w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none";
  const labelCls = "label-caps text-xs text-zinc-400";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <button onClick={() => navigate("/calendar")} className="flex items-center gap-2 text-zinc-400 hover:text-white label-caps text-sm" data-testid="back-btn">
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <div className="flex gap-2">
            <button data-testid="edit-event-btn" onClick={openEventEdit} className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-zinc-700 text-zinc-200 label-caps text-xs sm:text-sm rounded-sm hover:border-primary hover:text-primary">
              <Pencil size={16} /> {t("edit_event")}
            </button>
            <button data-testid="delete-event-btn" onClick={removeEvent} className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-red-900 text-red-400 label-caps text-xs sm:text-sm rounded-sm hover:bg-red-950/40">
              <Trash2 size={16} /> {t("delete")}
            </button>
          </div>
        </div>

        <div className="mb-8">
          <span className="label-caps text-xs px-2 py-0.5 bg-zinc-800 text-primary rounded-sm">{t(event.type)}</span>
          <h1 className="heading text-4xl font-bold text-white mt-2">{event.name}</h1>
          <p className="text-zinc-400 mt-2 flex flex-wrap items-center gap-4 text-sm">
            <span className="flex items-center gap-1"><CalendarDays size={16} /> {event.start_date}{event.end_date ? ` → ${event.end_date}` : ""}</span>
            {event.location && <span className="flex items-center gap-1"><MapPin size={16} /> {event.location}</span>}
            {event.url && <a href={event.url} target="_blank" rel="noreferrer" data-testid="event-link" className="flex items-center gap-1 text-primary hover:underline"><ExternalLink size={16} /> {t("open_link")}</a>}
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="label-caps text-sm text-zinc-400">{t("artists_seen")}</h2>
          <button data-testid="add-concert-btn" onClick={openAdd}
            className="flex items-center gap-2 px-3 py-2 text-primary border border-primary/40 label-caps text-sm rounded-sm hover:bg-primary/10">
            <Plus size={16} /> {t("add_concert")}
          </button>
        </div>

        {event.concerts.length === 0 ? (
          <p className="text-zinc-500 text-sm py-8 text-center">{t("no_concerts")}</p>
        ) : (
          <div className="space-y-6">
            {[
              { key: "seen", label: t("seen_list"), items: event.concerts.filter((c) => c.attended !== false) },
              { key: "missed", label: t("missed_list"), items: event.concerts.filter((c) => c.attended === false) },
            ].filter((g) => g.items.length > 0).map((group) => (
              <div key={group.key} data-testid={`concert-group-${group.key}`}>
                <h3 className="label-caps text-xs text-zinc-500 mb-2 flex items-center gap-2">
                  {group.key === "seen" ? <Eye size={14} className="text-green-400" /> : <EyeOff size={14} className="text-zinc-500" />}
                  {group.label} <span className="text-zinc-600">({group.items.length})</span>
                </h3>
                <div className="space-y-3">
                  {group.items.map((c) => {
                    const attended = c.attended !== false;
                    return (
                    <div key={c.id} data-testid="concert-item" className={`bg-zinc-900 border rounded-sm p-4 ${attended ? "border-zinc-800" : "border-zinc-800/50 opacity-60"}`}>
                      <div className="flex items-center justify-between">
                        <button onClick={() => c.band && navigate(`/bands/${c.band.id}`)} className="flex items-center gap-3 text-left">
                          <div className="w-12 h-12 bg-black border border-zinc-800 rounded-sm overflow-hidden flex items-center justify-center">
                            {c.band?.logo_url ? <img src={c.band.logo_url} alt="" className="w-full h-full object-cover" /> : <Skull size={20} className="text-zinc-700" />}
                          </div>
                          <div>
                            <p className="text-white heading text-lg flex items-center gap-2">
                              {c.band?.flag} {c.band?.name || "—"}
                              <span data-testid={`attended-badge-${c.id}`} className={`label-caps text-[10px] px-1.5 py-0.5 rounded-sm ${attended ? "bg-green-900/40 text-green-400" : "bg-zinc-800 text-zinc-500"}`}>
                                {attended ? t("attended_yes") : t("attended_no")}
                              </span>
                            </p>
                            <p className="text-zinc-500 text-xs">{c.date}</p>
                          </div>
                        </button>
                        <div className="flex items-center gap-3">
                          <button onClick={() => toggleAttended(c)} data-testid={`toggle-attended-${c.id}`} title={attended ? t("mark_not_seen") : t("mark_seen")}
                            className={`${attended ? "text-green-400 hover:text-green-300" : "text-zinc-600 hover:text-zinc-400"}`}>
                            {attended ? <Eye size={18} /> : <EyeOff size={18} />}
                          </button>
                          <StarRating value={c.rating || 0} size={16} testid={`concert-rating-${c.id}`} />
                          <button onClick={() => openEdit(c)} className="text-zinc-500 hover:text-primary" data-testid={`edit-concert-${c.id}`}><Pencil size={16} /></button>
                          <button onClick={() => removeConcert(c.id)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-concert-${c.id}`}><Trash2 size={16} /></button>
                        </div>
                      </div>
                      {c.comment && <p className="text-zinc-400 text-sm mt-3 border-t border-zinc-800 pt-3">{c.comment}</p>}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="label-caps text-sm text-zinc-400">{t("photos")}</h2>
            <label data-testid="add-photo-btn" className="flex items-center gap-2 px-3 py-2 text-primary border border-primary/40 label-caps text-sm rounded-sm hover:bg-primary/10 cursor-pointer">
              {uploadingPhoto ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} {t("add_photo")}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-file-input" />
            </label>
          </div>
          {(event.photos || []).length === 0 ? (
            <p className="text-zinc-500 text-sm py-6 text-center">{t("no_photos")}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {event.photos.map((p) => (
                <div key={p} data-testid="event-photo" className="relative group rounded-sm overflow-hidden border border-zinc-800 aspect-square bg-black">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(p)} data-testid="delete-photo-btn" title={t("delete_photo")}
                    className="absolute top-1 right-1 p-1.5 bg-black/70 text-red-400 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading text-2xl text-white">{editingId ? t("edit") : t("add_concert")}</h3>
              <button onClick={() => { setShowAdd(false); setEditingId(null); }} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>{t("select_band")}</label>
                {editingId ? (
                  <input data-testid="concert-band-fixed" value={bandQuery} readOnly className={inputCls + " opacity-70"} />
                ) : (
                  <div className="relative">
                    <input data-testid="concert-band-input" value={bandQuery} autoComplete="off"
                      onChange={(e) => { setBandQuery(e.target.value); setForm({ ...form, band_id: "" }); setShowBandSug(true); }}
                      onFocus={() => setShowBandSug(true)}
                      placeholder={t("search_band")} className={inputCls} />
                    {showBandSug && bandMatches.length > 0 && (
                      <div data-testid="concert-band-dropdown" className="absolute z-30 left-0 right-0 mt-1 bg-zinc-950 border border-zinc-700 rounded-sm max-h-56 overflow-y-auto shadow-xl">
                        {bandMatches.map((b) => (
                          <button key={b.id} type="button" data-testid={`concert-band-option-${b.id}`}
                            onClick={() => { setForm({ ...form, band_id: b.id }); setBandQuery(b.name); setShowBandSug(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-zinc-800 flex items-center gap-2 border-b border-zinc-800/60 last:border-0">
                            <span className="w-7 h-7 rounded-sm overflow-hidden bg-zinc-900 flex items-center justify-center shrink-0">
                              {b.logo_url ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" /> : <Skull size={14} className="text-zinc-700" />}
                            </span>
                            <span className="text-sm text-white truncate">{b.flag} {b.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div><label className={labelCls}>{t("date")}</label>
                {multiDay ? (
                  <select data-testid="concert-date-select" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls}>
                    {eventDates.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : (
                  <input data-testid="concert-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                )}
              </div>
              <div>
                <label className={labelCls}>{t("attended_label")}</label>
                <div className="flex gap-2 mt-1">
                  <button type="button" data-testid="attended-yes-btn" onClick={() => setForm({ ...form, attended: true })}
                    className={`flex-1 label-caps text-xs py-2 rounded-sm border transition-colors ${form.attended ? "border-green-500 text-green-400 bg-green-900/20" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                    {t("attended_yes")}
                  </button>
                  <button type="button" data-testid="attended-no-btn" onClick={() => setForm({ ...form, attended: false })}
                    className={`flex-1 label-caps text-xs py-2 rounded-sm border transition-colors ${!form.attended ? "border-primary text-primary bg-primary/10" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                    {t("attended_no")}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelCls}>{t("rating")}</label>
                <div className="mt-2"><StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} testid="concert-form-rating" /></div>
              </div>
              <div><label className={labelCls}>{t("comment")}</label><textarea data-testid="concert-comment-input" rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className={inputCls} /></div>
              <button data-testid="save-concert-btn" onClick={saveConcert} disabled={!form.band_id}
                className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 disabled:opacity-60">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      {showEventEdit && eventForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowEventEdit(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading text-2xl text-white">{t("edit_event")}</h3>
              <button onClick={() => setShowEventEdit(false)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div><label className={labelCls}>{t("event_name")}</label><input data-testid="edit-event-name" value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>{t("type")}</label>
                <select data-testid="edit-event-type" value={eventForm.type} onChange={(e) => setEventForm({ ...eventForm, type: e.target.value })} className={inputCls}>
                  <option value="concert">{t("concert")}</option>
                  <option value="festival">{t("festival")}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>{t("start_date")}</label><input data-testid="edit-event-start" type="date" value={eventForm.start_date} onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>{t("end_date")}</label><input data-testid="edit-event-end" type="date" value={eventForm.end_date} onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })} className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>{t("location")}</label><input data-testid="edit-event-location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>{t("event_url")}</label><input data-testid="edit-event-url" type="url" placeholder="https://..." value={eventForm.url} onChange={(e) => setEventForm({ ...eventForm, url: e.target.value })} className={inputCls} /></div>
              <button data-testid="save-event-edit-btn" onClick={saveEvent} className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400">{t("save_event")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
